import { Button } from '@c2fo/liquidity';
import { formatToolStepLabel, type ToolFeedback } from '../insightsUtils';

interface InsightsToolFeedbackPanelProps {
  currentActivity: ToolFeedback | null;
  toolFeedback: ToolFeedback[];
  isDebugExpanded: boolean;
  debugCopiedKey: string | null;
  onToggleDebug: () => void;
  onCopyDetails: (feedback: ToolFeedback) => void;
}

export const InsightsToolFeedbackPanel = ({
  currentActivity,
  toolFeedback,
  isDebugExpanded,
  debugCopiedKey,
  onToggleDebug,
  onCopyDetails,
}: InsightsToolFeedbackPanelProps) => {
  if (currentActivity === null && toolFeedback.length === 0) {
    return null;
  }

  return (
    <div className="thinking-panel" data-testid="tool-feedback-panel">
      <div className="thinking-panel-header">
        <div className="thinking-panel-header-main">
          {currentActivity ? (
            <>
              <div className="thinking-panel-inline">
                <span className={`thinking-dot status-${currentActivity.status}`}></span>
                <span className="thinking-panel-current-label">{formatToolStepLabel(currentActivity.label)}</span>
              </div>
              {isDebugExpanded && <div className="thinking-panel-subtitle">{currentActivity.summary}</div>}
            </>
          ) : (
            <>
              <div className="thinking-panel-title">Model thinking</div>
              {isDebugExpanded && <div className="thinking-panel-subtitle">Idle - no tool activity yet</div>}
            </>
          )}
        </div>

        {toolFeedback.length > 0 && (
          <Button
            type="button"
            variant="ancillary"
            size="sm"
            className="debug-toggle-button"
            onClick={onToggleDebug}
            aria-label={isDebugExpanded ? 'Hide debug details' : 'Show debug details'}
          >
            Debug {isDebugExpanded ? '▾' : '▴'}
          </Button>
        )}
      </div>

      {isDebugExpanded && toolFeedback.length > 0 && (
        <div className="thinking-steps">
          {toolFeedback
            .filter((feedback) => !(feedback.label === 'Tool call' && feedback.summary === 'Response captured'))
            .map((feedback) => (
              <details key={feedback.key} className="thinking-step">
                <summary>
                  <div className="thinking-step-headline">
                    <div className="thinking-step-main">
                      <span className="thinking-step-label">{formatToolStepLabel(feedback.label)}</span>
                      <span className={`thinking-step-status status-${feedback.status}`}>
                        {feedback.status === 'completed'
                          ? 'Done'
                          : feedback.status === 'running'
                            ? 'Running'
                            : 'Pending'}
                      </span>
                    </div>
                    <div className="thinking-step-actions">
                      <Button
                        type="button"
                        variant="ancillary"
                        size="sm"
                        className="thinking-step-copy-button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onCopyDetails(feedback);
                        }}
                      >
                        {debugCopiedKey === feedback.key ? 'Copied' : 'Copy'}
                      </Button>
                      <span className="thinking-step-caret" aria-hidden="true">
                        ▸
                      </span>
                    </div>
                  </div>
                  <p className="thinking-step-summary">{feedback.summary}</p>
                </summary>
                <pre>{feedback.details}</pre>
              </details>
            ))}
        </div>
      )}
    </div>
  );
};
